import React from 'react';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { useTranslation } from 'react-i18next';

// icons
import { CloudDownload, FolderPen, Trash2 } from 'lucide-react';

// types

// functions
import { displayFileSize } from '../../utils/functions';

// classes
import PleaseWait from '../../tools/please-wait';
import MiniButton from '../../tools/mini-button';

// types
import { CavernFileType } from '../../utils/types';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { setShowRenameFileModal } from '../../app-slice';
import { deleteFile } from '../data-management/file-browser-actions';
import { setDeletingFile } from './project-slice';
import { getUserProjectFiles } from './project-actions';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function CavernFilesTable() {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.project.loadingCavernFiles,
  );
  const sRenamingFile: boolean = useAppSelector((state) => state.project.renamingFile);
  const sDeletingFile: boolean = useAppSelector((state) => state.project.deletingFile);

  // the data items displayed in the table.
  const sUserItems: CavernFileType[] = useAppSelector((state) => state.project.userItems);

  // global slice.
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  const sErrorMessage: string = useAppSelector((state) => state.project.errorMessage);

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <>
      {sG_loggedIn !== true && (
        <div className="no-data-overlay">
          <span>Login required</span>
        </div>
      )}
      <div style={{ flex: '0 0 auto', marginLeft: '10px', width: '90%' }}>
        <PleaseWait text="Renaming file....." visible={sRenamingFile} />
      </div>
      <div style={{ flex: '0 0 auto', marginLeft: '10px', width: '90%' }}>
        <PleaseWait text="Deleting file....." visible={sDeletingFile} />
      </div>

      {sLoadingComponent === true && (
        <div style={{ flex: '0 0 auto', margin: '10px 0px 10px 10px', width: '90%' }}>
          <PleaseWait text={t('Loading, please wait') + '.....'} visible={true} />
        </div>
      )}

      {/* display the list of data items */}
      <DataItems key={sUserItems.length.toString()} errorMessage={sErrorMessage} />
    </>
  );
  //
} // CavernFilesTable

//  --------------------------------------------------------------------------
//
//  S U B   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Display an icon and name for each data item.
//
//  ------------------------------------------------------------

function DataItems(props: { errorMessage: string }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // the data items displayed in the table.
  const sUserItems: CavernFileType[] = useAppSelector((state) => state.project.userItems);
  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.project.loadingCavernFiles,
  );

  //  ------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  ------------------------------------------------------------

  const hasNoItems = Object.keys(sUserItems).length === 0;

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  if (hasNoItems === true && sLoadingComponent === false) {
    return (
      <div
        className="text-colour"
        data-muted="T"
        style={{
          flex: '0 0 60px',
          display: 'flex',
          justifyContent: 'left',
          alignItems: 'center',
          margin: '0px 0px 0px 10px',
        }}
      >
        No data items found
      </div>
    );
  }

  return (
    <>
      {sUserItems.map((item: CavernFileType, index: number) => {
        return (
          <div
            style={{
              flex: '0 0 60px',
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              margin: '0px 0px 0px 0px',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: '0 0 10px' }} />
            <MiniButton
              className="text-colour"
              data-mini-button="T"
              title="Download file"
            >
              <CloudDownload width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
            <div style={{ flex: '0 0 6px' }} />
            <MiniButton
              className="text-colour"
              data-mini-button="T"
              title="Rename file"
              onClick={() => {
                dispatch(setShowRenameFileModal({ show: true, filename: item.filename }));
              }}
            >
              <FolderPen width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
            <div style={{ flex: '0 0 6px' }} />
            <MiniButton
              className="text-colour"
              data-mini-button="T"
              title="Delete file"
              onClick={() => {
                dispatch(
                  deleteFile({
                    filename: item.filename,
                    storageType: 'user',
                    setLoadingFlag: setDeletingFile,
                    storagePath: [],
                    runAfterwards: getUserProjectFiles,
                  }),
                );
              }}
            >
              <Trash2 width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
            <div style={{ flex: '0 0 35px' }} />
            <div
              style={{
                flex: '1 1',
              }}
            >
              {item.filename ?? 'Unknown filename'}
            </div>
            <div style={{ flex: '0 0 10px' }} />
            <div
              className="text-colour"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 300px',
                alignItems: 'center',
                textAlign: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              {(item.fileType ?? '').toUpperCase()}
            </div>
            <div style={{ flex: '0 0 20px' }} />
            <div
              className="text-colour"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 200px',
                alignItems: 'center',
                textAlign: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              {displayFileSize({ bytes: item.length ?? 0 })}
            </div>
            <div style={{ flex: '0 0 50px' }} />
          </div>
        );
      })}
    </>
  );
} // DataItems
